import React from 'react';
import {
  Box,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  CircularProgress,
  Typography,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { buildEmailNotificationsSavePayload } from '../../utils/settingsForm';
import SettingsSectionCard from './SettingsSectionCard';

const NOTIFICATION_KEYS = [
  'orderReceived',
  'orderReady',
  'orderDelivered',
  'paymentReceived',
  'itemCancelled',
  'orderCancelled',
];

function EmailNotificationsSettingsSection({
  form,
  setField,
  onSave,
  onReset,
  loading,
  saving,
  error,
  success,
  setSuccess,
}) {
  const { t } = useTranslation();
  const settings = form.emailNotifications || {};

  if (loading) {
    return <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>;
  }

  const setNotificationField = (key, value) => {
    setField('emailNotifications', { ...settings, [key]: value });
  };

  const handleSave = () => {
    onSave(buildEmailNotificationsSavePayload(form));
  };

  return (
    <SettingsSectionCard
      title={t('settings.sections.emailNotifications')}
      description={t('settings.sections.emailNotificationsHelp')}
      error={error}
      success={success}
      successMessage={t('settings.saved')}
      onCloseSuccess={() => setSuccess(false)}
    >
      <Stack spacing={2}>
        <FormControlLabel
          control={(
            <Switch
              checked={settings.enabled === true}
              onChange={(e) => setNotificationField('enabled', e.target.checked)}
            />
          )}
          label={(
            <Box>
              <Typography variant="body2" fontWeight={600}>{t('settings.emailNotifications.enabled')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('settings.emailNotifications.enabledHelp')}</Typography>
            </Box>
          )}
        />

        <Divider />

        <Typography variant="subtitle2" color="text.secondary">
          {t('settings.emailNotifications.typesHeading')}
        </Typography>

        {NOTIFICATION_KEYS.map((key) => (
          <FormControlLabel
            key={key}
            disabled={!settings.enabled}
            control={(
              <Switch
                checked={settings[key] === true}
                onChange={(e) => setNotificationField(key, e.target.checked)}
              />
            )}
            label={(
              <Box>
                <Typography variant="body2" fontWeight={500}>{t(`settings.emailNotifications.${key}`)}</Typography>
                <Typography variant="caption" color="text.secondary">{t(`settings.emailNotifications.${key}Help`)}</Typography>
              </Box>
            )}
          />
        ))}

        <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
          <Button variant="outlined" onClick={onReset} disabled={saving}>{t('common.reset')}</Button>
        </Stack>
      </Stack>
    </SettingsSectionCard>
  );
}

export default EmailNotificationsSettingsSection;
